// @ts-nocheck
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import HeadingComponent, { HeadingProps } from "@store/Library/Components/Toolkit/Heading";
import useZoneIsActive from "@toolkit/Framework/Common/Hooks/useZoneIsActive";
import React, { FC } from "react";

const enum fields {
    usePageTitle = "usePageTitle",
    title = "title",
    variant = "variant",
    forwardAs = "forwardAs",
    textAlign = "textAlign",
    verticalAlign = "verticalAlign",
    orderLineExtendedPrice = "orderLineExtendedPrice",
    // Bunzl Custom Code
    showActionZone = "showActionZone",
}

interface Props extends WidgetProps {
    fields: {
        [fields.usePageTitle]: HeadingProps["usePageTitle"];
        [fields.title]: HeadingProps["title"];
        [fields.variant]: HeadingProps["variant"];
        [fields.forwardAs]: HeadingProps["forwardAs"];
        [fields.textAlign]: HeadingProps["textAlign"];
        [fields.verticalAlign]: HeadingProps["verticalAlign"];
        // Bunzl Custom Code
        [fields.showActionZone]: boolean;
    };
}

const Heading: FC<Props> = ({
    id,
    fields: { usePageTitle, title, variant, forwardAs, textAlign, verticalAlign, showActionZone },
}) => {
    const zoneIsActive = useZoneIsActive(id, "Content") && showActionZone;

    return (
        <HeadingComponent
            usePageTitle={usePageTitle}
            title={title}
            variant={variant}
            forwardAs={forwardAs}
            textAlign={textAlign}
            verticalAlign={verticalAlign}
        >
            {zoneIsActive && <Zone contentId={id} zoneName="Content" />}
        </HeadingComponent>
    );
};

const widgetModule: WidgetModule = {
    component: Heading,
    definition: {
        group: "Basic",
        icon: "heading",
        fieldDefinitions: [
            {
                name: fields.usePageTitle,
                displayName: "Use Page Title",
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
            },
            {
                name: fields.title,
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "",
                isVisible: x => !x.fields.usePageTitle,
            },
            /* Bunzl Custom Code - Start */
            {
                name: fields.showActionZone,
                displayName: "Show Action Zone",
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
            },
            /* Bunzl Custom Code - End */
            {
                name: fields.variant,
                displayName: "Format",
                fieldType: "General",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "None", value: "" },
                    { displayName: "Paragraph", value: "p" },
                    { displayName: "Heading 1", value: "h1" },
                    { displayName: "Heading 2", value: "h2" },
                    { displayName: "Heading 3", value: "h3" },
                    { displayName: "Heading 4", value: "h4" },
                    { displayName: "Heading 5", value: "h5" },
                    { displayName: "Heading 6", value: "h6" },
                ],
                defaultValue: "h1",
                hideEmptyOption: true,
            },
            {
                name: fields.forwardAs,
                displayName: "HTML Tag",
                fieldType: "General",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Match Format", value: "" },
                    { displayName: "span", value: "span" },
                    { displayName: "p", value: "p" },
                    { displayName: "h1", value: "h1" },
                    { displayName: "h2", value: "h2" },
                    { displayName: "h3", value: "h3" },
                    { displayName: "h4", value: "h4" },
                    { displayName: "h5", value: "h5" },
                    { displayName: "h6", value: "h6" },
                ],
                defaultValue: "",
                hideEmptyOption: true,
                tooltip: "Set a HTML tag that differs from the visual format for SEO purposes",
            },
            {
                name: fields.textAlign,
                displayName: "Alignment",
                fieldType: "General",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                defaultValue: "left",
                hideEmptyOption: true,
                isRequired: true,
            },
            {
                name: fields.verticalAlign,
                displayName: "Vertical Alignment",
                fieldType: "General",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top", value: "top" },
                    { displayName: "Middle", value: "middle" },
                    { displayName: "Bottom", value: "bottom" },
                ],
                defaultValue: "bottom",
                hideEmptyOption: true,
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
